import { useState } from "react";
import { useScopedPage } from "@/shared/hooks/use-scoped-page";
import { useInvitationsList } from "@/shared/invitations/use-invitations-list";
import type { InvitationStatus } from "@/shared/invitations/types";
import { hasForeignOrganization } from "./foreign-invitations";
import { OrganizationInvitationsSection } from "./OrganizationInvitationsSection";

interface OrganizationInvitationsPanelProps {
  readonly organizationId: string;
}

/**
 * The invitations of one organization, pending first. Mounted only once the
 * organization itself loaded, so an unknown id never lists anything.
 */
export function OrganizationInvitationsPanel({
  organizationId,
}: OrganizationInvitationsPanelProps) {
  const [status, setStatus] = useState<InvitationStatus | null>("PENDING");
  const [page, setPage] = useScopedPage(status ?? "");
  const { data, isLoading, isFetching, isError, error, refetch } = useInvitationsList({
    status,
    role: null,
    organizationId,
    page,
  });

  // Never render rows of another organization (a server that ignored the
  // filter would leak their invitee emails under this one's name).
  const foreign = hasForeignOrganization(data?.items, organizationId);

  return (
    <OrganizationInvitationsSection
      status={status}
      onStatusChange={setStatus}
      page={page}
      onPageChange={setPage}
      data={foreign ? undefined : data}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError || foreign}
      error={error}
      onRetry={() => void refetch()}
    />
  );
}
